import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../lib/apiClient";
import { FORM_MUTATION_META } from "../utils/formErrorHandler";
import type { TrendAlert } from "../utils/trendAnalysis";
import type { FormTemplate } from "./useFormTemplates";
import { invalidateTaskSurfaces } from "./taskSurfaces";

/**
 * US-029 / US-030 — submission auth_method discriminator. The default
 * `employee_session` covers every pre-US-029 row and any internal-audience
 * staff submission. The two external-provider values map to the two
 * shipped capture flows (supervised iPad + staff-uploaded artifact).
 */
export type FormSubmissionAuthMethod =
  | "employee_session"
  | "staff_supervised_external_provider"
  | "staff_uploaded_external_artifact";

export interface FormSubmission {
  id: string;
  form_template_id: string;
  template_version: number;
  facility_id: string;
  submitted_by: string;
  resident_id: string | null;
  employee_id: string | null;
  data: Record<string, unknown>;
  submission_date: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Joined data
  form_template?: Partial<FormTemplate>;
  submitted_by_user?: { email: string };
  resident?: { first_name: string; last_name: string };
  employee?: { first_name: string; last_name: string };
  // US-029: external-provider context.
  auth_method?: FormSubmissionAuthMethod;
  external_provider_id?: string | null;
  external_assignment_id?: string | null;
  witnessed_by_employee_id?: string | null;
  signature_s3_key?: string | null;
  wet_signature_s3_key?: string | null;
  // US-030: witness display name resolved by the backend list GET.
  witnessed_by_employee?: { first_name: string; last_name: string } | null;
}

interface FormSubmissionFilters {
  facility_id?: string;
  form_template_id?: string;
  resident_id?: string;
  employee_id?: string;
  /** Scope to submissions of templates with this audience, e.g. "staff". */
  audience?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
}

/**
 * Get form submissions
 * @param suppressErrorNotification - SCRUM-141: true for secondary/best-effort
 *   widgets (e.g. TrendAlertsSection on the resident overview) — an assumed-role
 *   test session can legitimately lack access here (expected 403), so it should
 *   render the widget's own empty state instead of a page-interrupting toast.
 */
export function useFormSubmissions(
  filters?: FormSubmissionFilters,
  suppressErrorNotification = false,
) {
  return useQuery<FormSubmission[]>({
    queryKey: ["form-submissions", filters],
    meta: { suppressErrorNotification },
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.facility_id)
        params.append("facility_id", filters.facility_id);
      if (filters?.form_template_id)
        params.append("form_template_id", filters.form_template_id);
      if (filters?.resident_id)
        params.append("resident_id", filters.resident_id);
      if (filters?.employee_id)
        params.append("employee_id", filters.employee_id);
      if (filters?.audience) params.append("audience", filters.audience);
      if (filters?.start_date) params.append("start_date", filters.start_date);
      if (filters?.end_date) params.append("end_date", filters.end_date);
      if (filters?.limit) params.append("limit", filters.limit.toString());

      const { data } = await api.get(`/form-submissions?${params.toString()}`);
      return data.data;
    },
  });
}

/**
 * Server-computed form-submission trend alerts for every active resident in a
 * facility (90-day window). Replaces pulling raw submissions to the browser and
 * analyzing client-side — that capped by row count across the whole facility
 * and starved per-resident series. Disabled until a facility is known.
 */
export function useFacilityFormTrends(facilityId: string | null | undefined) {
  return useQuery({
    queryKey: ["form-trends", facilityId],
    queryFn: async () => {
      const { data } = await api.get(`/facilities/${facilityId}/form-trends`);
      return data.data as TrendAlert[];
    },
    enabled: !!facilityId,
    staleTime: 5 * 60 * 1000, // trends move slowly; avoid refetch churn
  });
}

/**
 * Submit a form
 */
export function useSubmitForm() {
  const queryClient = useQueryClient();

  return useMutation({
    // US-081: dynamic-form surfaces own the error UX — every caller's catch
    // routes through handleDynamicFormSubmitError (400 details → inline on
    // the renderer, everything else → one toast). Suppress the global toast.
    meta: FORM_MUTATION_META,
    mutationFn: async (submission: {
      form_template_id: string;
      facility_id: string;
      data: Record<string, unknown>;
      resident_id?: string;
      employee_id?: string;
      submission_date?: string;
      notes?: string;
      /** When filling a schema-driven REQUIRED form, links + fulfils it. */
      requirement_id?: string;
      /** US-115: the draft being finalised — deleted server-side on success. */
      draft_id?: string;
    }) => {
      const { data } = await api.post("/form-submissions", submission);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["form-submissions"] });
      // US-115: a finalised submission consumes its draft.
      queryClient.invalidateQueries({ queryKey: ["form-drafts"] });
      queryClient.invalidateQueries({ queryKey: ["form-templates"] }); // Refresh "due" status
      // Refresh requirement rows + their submission history (fulfilled status).
      queryClient.invalidateQueries({
        queryKey: ["resident-form-requirements"],
      });
      queryClient.invalidateQueries({
        queryKey: ["resident-form-requirement-submissions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["resident-form-requirement-detail"],
      });
      // A required form can also be an appointment's required-form: refresh the
      // appointment closure-gate snapshot and the calendar "needs an
      // appointment" gap panel so neither lingers as pending after a fill.
      queryClient.invalidateQueries({
        queryKey: ["appointment-required-forms"],
      });
      queryClient.invalidateQueries({
        queryKey: ["resident-appointment-form-gaps"],
      });
      // US-153/F-134: a filed form completes the derived `form` task on the
      // resident board. This hook already refreshes eight related surfaces and
      // still missed that one — the board reads its own key.
      invalidateTaskSurfaces(queryClient);
    },
  });
}

/**
 * Export form submissions to CSV
 */
export function useExportFormSubmissions() {
  return useMutation({
    // US-081: error fallback centralized here; FormSubmissionsPage catch is sole caller.
    meta: { errorFallback: "Failed to export form submissions" },
    mutationFn: async (filters?: FormSubmissionFilters) => {
      const params = new URLSearchParams();
      params.append("format", "csv");
      if (filters?.form_template_id)
        params.append("form_template_id", filters.form_template_id);
      if (filters?.resident_id)
        params.append("resident_id", filters.resident_id);
      if (filters?.employee_id)
        params.append("employee_id", filters.employee_id);
      if (filters?.start_date) params.append("start_date", filters.start_date);
      if (filters?.end_date) params.append("end_date", filters.end_date);

      const response = await api.get(
        `/form-submissions/export?${params.toString()}`,
        {
          responseType: "blob",
        },
      );

      // Download the file
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `form-submissions-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return response.data;
    },
  });
}
